import React, { useEffect, useState } from "react";
import useSize from "../../../../Utils/useSize/useSize";
import Backend from "../../../../Backend/Backend";
import toast from "react-hot-toast";
import TopHeader from "./TopHeader";
import Loading from "../../../../Utils/Loading/Loading";

const fmt = require("indian-number-format");

export default function Profile() {
  const windowSize = useSize();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backend, setBackend] = useState(null);

  useEffect(() => {
    (async () => {
      const b = await new Backend().init();
      setBackend(b);
    })();
  }, []);

  useEffect(() => {
    if (backend) loadProfile();
  }, [backend]);

  const loadProfile = async () => {
    setLoading(true);
    const data = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
    };

    try {
      const response = await backend.profile(data);
      if (response.error === "False") setProfile(response.profile);
      else toast.error(response.message);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load profile.");
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <Loading height={windowSize[1] - 70} />
    );

  if (!profile) return null;

  const InfoRow = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
      <div className="flex justify-between items-center py-1">
        <span className="text-ls flex-1 text-[var(--text)] font-semibold opacity-80">
          {label}
        </span>
        <span className="text-xs flex-1 text-right uppercase font-bold text-[var(--text)]">
          {value}
        </span>
      </div>
    );
  };

  const Section = ({ title, children }) => (
    <div className="bg-[var(--background)]/80 rounded-xl p-3 shadow-sm border-2 border-[var(--hover)]">
      <h3 className="text-sm font-semibold mb-2 border-b pb-1 border-[var(--hover)] text-[var(--primary)]">
        {title}
      </h3>
      {children}
    </div>
  );

  return (
    <div className="flex flex-col">
      <TopHeader profile={profile} />
      <div
        style={{
          height: windowSize[1] - 70,
          paddingBottom : 200
        }}
        className="overflow-y-auto p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
      >
        <Section title="Account">
          <InfoRow label="Server" value={profile.server} />
          <InfoRow label="Username" value={profile.username} />
          <InfoRow label="Name" value={profile.name} />
        </Section>

        <Section title="Financials">
          <InfoRow
            label="Credit"
            value={`${fmt.format(
              parseFloat(profile.deposit_total || 0).toFixed(2)
            )}`}
          />
          <InfoRow
            label="Balance"
            value={`${fmt.format(
              parseFloat(profile.deposit_current || 0).toFixed(2)
            )}`}
          />
          {profile.nse_cash === 1 && (
            <>
              <InfoRow
                label="Deposit"
                value={`${fmt.format(
                  parseFloat(profile.deposit_interest || 0).toFixed(2)
                )}`}
              />
              <InfoRow
                label="Deposit Interest"
                value={`${fmt.format(
                  parseFloat(
                    (profile.deposit_total || 0) -
                      (profile.deposit_interest || 0)
                  ).toFixed(2)
                )}`}
              />
              <InfoRow
                label="Intrest Rate %"
                value={`${fmt.format(
                  parseFloat(profile.deposit_interest_rate || 0).toFixed(2)
                )} %`}
              />
            </>
          )}
        </Section>

        <Section title="Exchange Access">
          <InfoRow
            label="Active Exchanges"
            value={
              <>
                {profile.nse ? "NSE " : ""}
                {profile.mcx ? "MCX " : ""}
                {profile.mcx_options ? "MCX Options " : ""}
                {profile.options ? "Nse Options " : ""}
                {profile.nse_cash ? "NSE Equity " : ""}
                {profile.comex ? "Comex " : ""}
                {profile.crypto ? "Crypto " : ""}
                {profile.forex ? "Forex " : ""}
                {profile.sgx ? "SGX " : ""}
                {profile.us_stock ? "Us-Stock " : ""}
                {profile.index ? "Others " : ""}
                {profile.dgcx ? "Dgcx " : ""}
                {profile.ncdex ? "Ncdex " : ""}
              </>
            }
          />
        </Section>

        <Section title="Commission">
          {profile.nse === 1 && <InfoRow label="Nse" value={profile.b_nse} />}
          {profile.mcx === 1 && (
            <InfoRow
              label="Mcx"
              value={`${profile.b_mcx_lot} Lot / ${profile.b_mcx_volume} Vol`}
            />
          )}
          {profile.mcx_options === 1 && (
            <InfoRow label="Mcx Options" value={profile.b_mcx_options} />
          )}
          {profile.options === 1 && (
            <InfoRow label="Nse Options" value={profile.b_options} />
          )}
          {profile.nse_cash === 1 && (
            <InfoRow label="Nse Equity" value={profile.b_nse_cash} />
          )}
          {profile.comex === 1 && (
            <InfoRow label="Comex" value={`$ ${profile.b_comex}`} />
          )}
          {profile.crypto === 1 && (
            <InfoRow label="Crypto" value={`$ ${profile.b_crypto}`} />
          )}
          {profile.forex === 1 && (
            <InfoRow label="Forex" value={`$ ${profile.b_forex}`} />
          )}
          {profile.sgx === 1 && (
            <InfoRow label="SGX" value={`$ ${profile.b_sgx}`} />
          )}
          {profile.us_stock === 1 && (
            <InfoRow label="Us Stock" value={`$ ${profile.b_us_stock}`} />
          )}
          {profile.index === 1 && (
            <InfoRow label="Others" value={`$ ${profile.b_index}`} />
          )}
          {profile.dgcx === 1 && (
            <InfoRow label="Dgcx" value={`$ ${profile.b_dgcx}`} />
          )}
          {profile.ncdex === 1 && (
            <InfoRow label="Ncdex" value={`$ ${profile.b_ncdex}`} />
          )}
        </Section>

        <Section title="Margin">
          {profile.nse === 1 && (
            <InfoRow label="Nse" value={profile.margin_nse} />
          )}
          {profile.mcx === 1 && (
            <InfoRow label="Mcx" value={profile.margin_mcx} />
          )}
          {profile.mcx_options === 1 && (
            <InfoRow label="Mcx Options" value={profile.margin_mcx_options} />
          )}
          {profile.options === 1 && (
            <InfoRow label="Nse Options" value={profile.margin_options} />
          )}
          {profile.nse_cash === 1 && (
            <InfoRow label="Nse Equity" value={profile.margin_nse_cash} />
          )}
          {profile.comex === 1 && (
            <InfoRow label="Comex" value={profile.margin_comex} />
          )}
          {profile.crypto === 1 && (
            <InfoRow label="Crypto" value={profile.margin_crypto} />
          )}
          {profile.forex === 1 && (
            <InfoRow label="Forex" value={profile.margin_forex} />
          )}
          {profile.sgx === 1 && (
            <InfoRow label="Sgx" value={profile.margin_sgx} />
          )}
          {profile.us_stock === 1 && (
            <InfoRow label="Us Stock" value={profile.margin_us_stock} />
          )}
          {profile.index === 1 && (
            <InfoRow label="Others" value={profile.margin_index} />
          )}
          {profile.dgcx === 1 && (
            <InfoRow label="Dgcx" value={profile.margin_dgcx} />
          )}
          {profile.ncdex === 1 && (
            <InfoRow label="Ncdex" value={profile.margin_ncdex} />
          )}
        </Section>
      </div>
    </div>
  );
}
